import { HelpCircle, ChevronRight } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

// ──────────────────────────────────────────────────────────────────────
// Iter 61 Session B Part 5 — Dashboard Widget Overhaul
// Info-icon tooltip + "view all" footer link + per-widget metadata
// used by TaskDashboardPage.
// ──────────────────────────────────────────────────────────────────────

export const WIDGET_META = {
  capacity: {
    description: 'Your rolling daily workload. Green bar fills as you log active hours toward your baseline capacity.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'Plan my day →',
  },
  my_tasks: {
    description: 'Your assigned tasks, newest-first. Click any row to open details, priority dot shows urgency.',
    viewAllPath: '/tasks/all?tab=my-tasks',
    viewAllLabel: 'View all my tasks →',
  },
  overdue: {
    description: 'Tasks you are assigned to that are past their due date. Click to jump into a task.',
    viewAllPath: '/tasks/all?filter=overdue',
    viewAllLabel: 'View all overdue →',
  },
  due_soon: {
    description: 'Tasks due today, tomorrow, or the day after. Status (Not Started, In Progress, Waiting, Review) does not matter — only completed/cancelled are excluded.',
    viewAllPath: '/tasks/all?filter=due_soon',
    viewAllLabel: 'View all due soon →',
  },
  // 2026-02-20 — `upcoming_followups` was briefly shipped as a widget
  // but moved to a fixed alert banner at the top of the dashboard.
  // Entry removed from the Customize catalog.
  activity: {
    description: 'Real-time team activity stream. Click a name to see that person\'s full activity log.',
    // BUG-D06 — was '/tasks/agents' (wrong destination — AI Agents page). No
    // dedicated activity-log route exists yet; null path + null label suppresses
    // the footer link entirely until one is built.
    viewAllPath: null,
    viewAllLabel: null,
  },
  workload_status: {
    description: 'How tasks are distributed across statuses. Click a status to filter the tasks page.',
    viewAllPath: '/tasks/all?tab=by-status',
    viewAllLabel: 'Open Kanban →',
  },
  workload_assignee: {
    description: 'Hours booked per team member this week. Click a name to view their tasks.',
    viewAllPath: '/tasks/team-view',
    viewAllLabel: 'Team view →',
  },
  priority_breakdown: {
    description: 'Count of active tasks at each priority level. Click a number to filter tasks.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'View all tasks →',
  },
  calculation: {
    description: 'Total open tasks, completed count, and completion rate for this workspace.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'View all tasks →',
  },
  time_tracking: {
    description: 'Hours logged by your team in the current reporting window.',
    viewAllPath: '/tasks/report',
    viewAllLabel: 'Full timesheet →',
  },
  project_progress: {
    description: 'Completion progress per project / client. Click a name to open the project.',
    viewAllPath: '/tasks/projects',
    viewAllLabel: 'All projects →',
  },
  recent_completed: {
    description: 'Tasks completed in the last 7 days. Click to review what shipped.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'View all completed →',
  },
  upcoming_deadlines: {
    description: 'Upcoming task deadlines sorted by date. Click a row to open the task.',
    viewAllPath: '/tasks/calendar',
    viewAllLabel: 'Open calendar →',
  },
  ai_suggestions: {
    description: 'Smart recommendations based on your workload, deadlines, and activity.',
    viewAllPath: '/tasks/ai-chat',
    viewAllLabel: 'Ask the assistant →',
  },
  notes: {
    description: 'Your personal notes and reminders. Click a note to open, or create a new one.',
    viewAllPath: '/tasks/notes',
    viewAllLabel: 'All my notes →',
  },
  discussion: {
    description: 'Recent comments across tasks and submissions. Click to jump to that conversation.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'All discussions →',
  },
  team_monitor: {
    description: 'Live hours per teammate (Admin only). Click a name to view their day.',
    viewAllPath: '/tasks/team-view',
    viewAllLabel: 'Team view →',
  },
  team_availability: {
    description: 'Who is on approved leave today or this week. Click a name to see their request.',
    viewAllPath: '/tasks/availability',
    viewAllLabel: 'Availability calendar →',
  },
  team_report: {
    description: 'Completion rates per team member in the reporting window.',
    viewAllPath: '/tasks/report',
    viewAllLabel: 'Full report →',
  },
  calendar_preview: {
    description: 'Upcoming tasks shown on the calendar. Click any row to open task details.',
    viewAllPath: '/tasks/calendar',
    viewAllLabel: 'Full calendar →',
  },
  clients: {
    description: 'Quick overview of your client roster. Click a client to open their workspace.',
    viewAllPath: '/tasks/clients',
    viewAllLabel: 'All clients →',
  },
  integrations: {
    description: 'Connected external services (Google Drive, Meta Pixel, Notion, etc.).',
    viewAllPath: '/tasks/integrations',
    viewAllLabel: 'Manage integrations →',
  },
  ai_insight: {
    description: 'A pinned insight from an AI chat session, refreshable from the source conversation.',
    viewAllPath: '/tasks/ai-chat',
    viewAllLabel: 'Open full chat →',
  },
  timesheet: {
    description: 'Team hours summary for the selected window. Click a member to see their activity.',
    viewAllPath: '/tasks/report',
    viewAllLabel: 'Full timesheet →',
  },
  drive_files: {
    description: 'Recently edited Google Drive files. Click any row to open in a new tab.',
    viewAllPath: '/tasks/integrations',
    viewAllLabel: 'Drive settings →',
  },
  calendar_meetings: {
    description: 'Upcoming meetings from your connected Google Calendar.',
    viewAllPath: '/tasks/calendar',
    viewAllLabel: 'Calendar →',
  },
  team_pulse: {
    description: 'Live clock-in / online / active-task status per teammate. Updates every 15s.',
    viewAllPath: '/tasks/team-view',
    viewAllLabel: 'Team view →',
  },
  notifications: {
    description: 'Recent important notifications — transfer requests, mentions, comments, reviews. Low-value reminders are filtered out. Click any item to open the related task.',
    viewAllPath: '/tasks/notifications',
    viewAllLabel: 'All notifications →',
  },
  task_list: {
    description: 'Full task list shortcut.',
    viewAllPath: '/tasks/all',
    viewAllLabel: 'All tasks →',
  },
};

export function widgetMeta(cardType) {
  return WIDGET_META[cardType] || { description: '', viewAllPath: null, viewAllLabel: null };
}

/**
 * WidgetInfo — tiny "?" icon rendered next to the widget title.
 * Clicking it opens a Radix Popover with a short description of what the
 * widget does + why it's useful. Matches Safari pointer-events pattern
 * from the rest of the app (see StellarFormControls.jsx comment).
 */
export function WidgetInfo({ description, cardType }) {
  const text = description || widgetMeta(cardType).description || '';
  if (!text) return null;
  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="p-0.5 rounded-full text-muted-foreground/40 hover:text-gold hover:bg-foreground/[0.05] transition-colors"
          aria-label="What does this widget show?"
          title="What does this widget show?"
          data-testid={`widget-info-${cardType}`}
        >
          <HelpCircle className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="pointer-events-auto w-64 p-3 text-[12px] leading-relaxed text-muted-foreground bg-card border border-border/50"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

/**
 * WidgetFooter — standardised "View all →" link at the bottom of list
 * widgets. Keeps the CTA visually consistent across every card.
 */
export function WidgetFooter({ cardType, onClick, label, testId }) {
  const meta = widgetMeta(cardType);
  const text = label || meta.viewAllLabel;
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-[11px] text-gold/70 hover:text-gold mt-2 pt-1.5 border-t border-border/20 w-full transition-colors"
      data-testid={testId || `widget-footer-${cardType}`}
    >
      <span>{text}</span>
      <ChevronRight className="h-3 w-3 ml-auto opacity-60" />
    </button>
  );
}
